import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  GrowthBookUser,
  getGrowthBookValue,
  identifyGrowthBookUser,
  isGrowthBookLoaded,
  loadGrowthBook,
  setGrowthBookPageAttributes,
} from "app/library/growthbook";

export type PaywallFlagKey =
  | "paywall-pro"
  | "paywall-academy"
  | "paywall-analyze";

// A trial paywall's marketing copy, which growth edits and A/B tests through
// the paywall's GrowthBook flag. Prices and trial terms never come from the
// flag. The flag's `default_billing` (annual or monthly) picks the term the
// page opens on; see paywallBillingTerm.
export interface PaywallContent {
  headline: string;
  subhead: string;
  // Empty hides the badge.
  annual_badge: string;
  plan_description: string;
  trial_headline: string;
  cta_text: string;
  show_free_plan_button: boolean;
  free_plan_text: string;
  // The right panel's copy. Only Analyze builds that panel in code; the PRO
  // and Academy panels are a single image, so these stay unset there.
  social_proof_headline?: string;
  feature_card_tagline?: string;
  feature_list?: string[];
}

export type BillingTerm = "annual" | "monthly";

const TRIAL_DAYS = /{{\s*trialDays\s*}}/g;

const asObject = (value: unknown): Record<string, unknown> | null =>
  value != null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const isTextList = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === "string");

/**
 * Lays the flag over the paywall's defaults (today's copy), field by field.
 * A field that is missing or has the wrong type keeps its default, so a bad
 * flag can never break the paywall. `{{trialDays}}` in the flag's text
 * becomes the offer's trial length, which still comes from the API.
 */
export function mergePaywallContent<T extends PaywallContent>(
  defaults: T,
  flag: unknown,
  trialDays: number,
): T {
  const fields = asObject(flag);
  if (fields == null) {
    return defaults;
  }
  const withTrialDays = (text: string) =>
    text.replace(TRIAL_DAYS, String(trialDays));
  const content = { ...defaults };
  (Object.keys(defaults) as Array<keyof T & string>).forEach((key) => {
    const field = fields[key];
    if (Array.isArray(defaults[key])) {
      if (isTextList(field)) {
        content[key] = field.map(withTrialDays) as any;
      }
    } else if (typeof field === typeof defaults[key]) {
      content[key] = (typeof field === "string"
        ? withTrialDays(field)
        : field) as any;
    }
  });
  return content;
}

// The billing term the flag opens the page on, if it names a valid one.
export function paywallBillingTerm(flag: unknown): BillingTerm | undefined {
  const term = asObject(flag)?.default_billing;
  return term === "annual" || term === "monthly" ? term : undefined;
}

interface Options {
  // Whether the trial paywall is what the user sees. Reading the flag is what
  // counts them as in the test, so it waits until then.
  shown: boolean;
  // `locale` is the account's language, which decides who may read the flag.
  user?: (GrowthBookUser & { locale?: string }) | null;
  // Whether the link named the billing term (it then wins over the flag's
  // default_billing). Sent as billing_term_in_url, so a test of the default
  // term can leave those users out.
  billingTermInUrl?: boolean;
}

/**
 * The paywall's GrowthBook flag, read once the paywall shows. `ready` is
 * false while the flags load (2 seconds at most), so the paywall can hold its
 * content instead of flashing the defaults before the variant.
 */
export function usePaywallFlag(
  flagKey: PaywallFlagKey,
  { shown, user, billingTermInUrl = false }: Options,
) {
  const { i18n } = useTranslation();
  // The flags hold English copy: other languages keep their translated
  // defaults and never read a flag, so they're never counted in a test. The
  // account's language decides, because the page keeps its old language
  // until the account's translations download. Without one, the page's
  // language decides once any switch has settled.
  const pageLanguage = i18n?.resolvedLanguage || i18n?.language || "en";
  const language = user?.locale || pageLanguage;
  // Set by i18next for the length of a switch (react-i18next reads it too),
  // but missing from its types.
  const switchingTo = (i18n as unknown as { isLanguageChangingTo?: string })
    ?.isLanguageChangingTo;
  const settling = !user?.locale && Boolean(switchingTo);
  const holding = shown && settling;
  const enabled = shown && !settling && language.split("-")[0] === "en";
  const [loaded, setLoaded] = useState(isGrowthBookLoaded);

  useEffect(() => {
    if (!enabled || loaded) {
      return undefined;
    }
    let active = true;
    loadGrowthBook().then(() => {
      if (active) {
        setLoaded(true);
      }
    });
    return () => {
      active = false;
    };
  }, [enabled, loaded]);

  // Read when the paywall shows, not on every render.
  const flag = useMemo(() => {
    if (!enabled || !loaded) {
      return undefined;
    }
    identifyGrowthBookUser(user);
    setGrowthBookPageAttributes({ billing_term_in_url: billingTermInUrl });
    return getGrowthBookValue<unknown>(flagKey, {});
  }, [enabled, loaded, flagKey, user, billingTermInUrl]);

  return { flag, ready: !holding && (!enabled || loaded) };
}
